import { useCallback, useMemo, useRef, useState } from 'react'
import type { ModalContextType } from 'contexts/ModalContext'
import type { BookableFlightOption } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/useBookShortlistedFlight'
import useBookShortlistedFlight from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/useBookShortlistedFlight'
import type { FlightRecommendationSingleItem, FlightOption } from './chatFlightTypes'
import { getFlightOptionJourneyKey } from './chatFlightTypes'

function buildSavedItemData(item: FlightRecommendationSingleItem, option: FlightOption) {
  return {
    source: 'le_flight' as const,
    journeyKey: getFlightOptionJourneyKey(option),
    searchId: item.searchId ?? option.savedItemData?.searchId,
    searchExpiry: item.searchExpiry ?? option.savedItemData?.searchExpiry,
    searchParams: item.searchParams ?? option.savedItemData?.searchParams,
    price: option.price,
    currencyCode: item.currencyCode,
    carrierLogo: option.carrierBrandMark ?? option.carrierLogo,
    airline: option.carrier,
    flightNumber: option.leg.flightNumber,
    departureAirport: option.leg.departureAirport,
    arrivalAirport: option.leg.arrivalAirport,
    departureTime: option.leg.departureTime,
    arrivalTime: option.leg.arrivalTime,
    duration: `${option.leg.durationHours}h ${option.leg.durationMinutes}m`,
    layovers: option.leg.layovers.map((layover) => ({
      airportCode: layover.airportCode,
      airportName: layover.airportName,
      duration: layover.durationHours !== undefined && layover.durationMinutes !== undefined
        ? `${layover.durationHours}h ${layover.durationMinutes}m`
        : undefined,
    })),
  }
}

function buildTransportOption(item: FlightRecommendationSingleItem, index: number): BookableFlightOption {
  const outbound: BookableFlightOption = {
    itemId: -1 - (index * 2),
    savedItemData: buildSavedItemData(item, item.outbound),
  }

  if (item.inbound) {
    outbound.returnLeg = {
      itemId: -2 - (index * 2),
      savedItemData: buildSavedItemData(item, item.inbound),
    }
  }

  return outbound
}

export default function useFlightRecommendationBooking(
  item: FlightRecommendationSingleItem,
  index: number,
  showModal: ModalContextType,
) {
  const { bookFlight } = useBookShortlistedFlight(showModal)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUnavailable, setIsUnavailable] = useState(false)
  const isSubmittingRef = useRef(false)

  const transportOption = useMemo(
    () => buildTransportOption(item, index),
    [index, item],
  )

  const handleSelect = useCallback(async() => {
    if (isSubmittingRef.current || isUnavailable) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      const result = await bookFlight(transportOption)
      if (result === 'unavailable') {
        setIsUnavailable(true)
      }
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }, [bookFlight, isUnavailable, transportOption])

  return {
    handleSelect,
    isSubmitting,
    isUnavailable,
  }
}
